import { useEffect, useState, useCallback } from "react";
import { Loader } from "shared";
import axios from "axios";
import { BasketKPLabels, TBasketData } from "../BasketKP.types";
import * as R from "ramda";
import ConfirmModal from "../../../../shared/ConfirmModal";
import { formatNumber } from "helpers";
import Mustache from "mustache";
import { useMedia, useQueryParams } from "hooks";
import BasketItem from "../BasketItem";
import VoucherItem from "../VoucherItem";

type TBasketPageProps = {
  labels: BasketKPLabels;
  domain: string;
  domainPath: string;
  pageId: string;
};

const BasketErrorQueryParam = "error";

function BasketPage({ labels, domain, domainPath, pageId }: TBasketPageProps) {
  const { queryParams, setQueryParams } = useQueryParams();
  const [basketData, setBasketData] = useState<TBasketData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [basketError, setBasketError] = useState("");
  const [isEmailPopup, setIsEmailPopup] = useState<boolean>(
    labels.ShowEmailValidatedMessage,
  );

  const [childErrors, setChildErrors] = useState({});

  const handleErrorChange = useCallback((id: number, hasError: boolean) => {
    setChildErrors((prev) => ({
      ...prev,
      [id]: hasError,
    }));
  }, []);

  const hasAnyErrors = Object.values(childErrors).some(Boolean);

  const qsErrorCode = R.prop(BasketErrorQueryParam, queryParams);

  const { isTablet } = useMedia();

  useEffect(() => {
    checkBasketError();
    // eslint-disable-next-line
  }, [basketData]);

  const checkBasketError = () => {
    const errorCode = qsErrorCode ? Number(qsErrorCode) : basketData?.ErrorCode;
    if (errorCode !== null && errorCode !== undefined && errorCode !== -1) {
      setBasketError(
        (R.path(["BasketErrors", errorCode], labels) ||
          labels.GenericErrorText) as string,
      );
    } else {
      setBasketError("");
    }
  };

  useEffect(() => {
    setIsLoading(true);
    axios
      .get(domain + domainPath, {
        withCredentials: true,
      })
      .then((res) => {
        setBasketData(res.data);
        if (res.data.ErrorCode === 4) {
          const basketHeaderElem = document.querySelectorAll(
            '[data-basketproperty="TotalItemsCount"]',
          );
          if (basketHeaderElem) {
            basketHeaderElem.forEach((elem) => {
              elem.textContent = "0";
            });
          }
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", isSticky);

    return () => {
      window.removeEventListener("scroll", isSticky);
    };
  }, []);

  const mapBasketItemsForGTM = (basketData: TBasketData) => {
    return basketData.PerformanceGroups.flatMap((group) =>
      group.BasketPackagesAndExtras.map((pkg) => ({
        item_id: `${pkg.ProductId}-${pkg.AudienceSubCategoryId}`,
        item_name: pkg.ProductName,
        price: pkg.PriceWithoutVat,
        quantity: pkg.BasketQuantity,
        item_category:
          group.SitecorePerformanceDescription.split("|").length > 1
            ? group.SitecorePerformanceDescription.split("|")[1].trim()
            : "Unknown Venue",
        item_category2: group.SitecorePerformance,
        item_category3:
          pkg.ProductName.indexOf(" - ") > 0
            ? pkg.ProductName.split(" - ")[0].trim()
            : pkg.ProductName,
        item_category5: pkg.Quantity.toString(),
      })),
    );
  };

  const pushViewCart = (basketData: TBasketData) => {
    const items = mapBasketItemsForGTM(basketData);
    if (!items.length) {
      console.warn("ViewCart: Basket data not defined or empty");
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "view_cart",
      ecommerce: {
        value: basketData.TotalExcVAT,
        currency: "GBP",
        items,
      },
    });
  };

  const pushBeginCheckout = (basketData: TBasketData) => {
    const items = mapBasketItemsForGTM(basketData);
    if (!items.length) {
      console.warn("BeginCheckout: Basket data not defined or empty");
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "begin_checkout",
      ecommerce: {
        value: basketData.TotalExcVAT,
        currency: "GBP",
        items,
      },
    });
  };

  useEffect(() => {
    if (!basketData) return;
    pushViewCart(basketData);
  }, [basketData]);

  const isSticky = () => {
    const stickyAnchor = document
      .querySelector(".o-basket__bottom-bar-anchor")
      ?.getBoundingClientRect()?.top;

    if (!stickyAnchor) return;

    const stickyElement = document.querySelector(".o-basket__bottom-bar");

    if (
      window.innerHeight <
      stickyAnchor + Number(stickyElement?.getBoundingClientRect().height)
    ) {
      stickyElement?.classList.add("u-sticky");
    } else {
      stickyElement?.classList.remove("u-sticky");
    }
  };

  const onCheckout = () => {
    setIsLoading(true);
    pushBeginCheckout(basketData!);
    axios
      .request({
        method: "POST",
        url: domain + `/api/sitecore/SecutixCheckout/Checkout`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        withCredentials: true,
        data: {
          Events: basketData?.Events,
          PageId: pageId,
          Products: [],
        },
      })
      .then((res) => {
        console.log(res);
        if (res.data.RedirectUrl) {
          window.location.href = res.data.RedirectUrl;
        } else {
          if (res.data.ErrorCode) {
            setBasketError(
              (R.path(["BasketErrors", res.data.ErrorCode], labels) ||
                labels.GenericErrorText) as string,
            );
          }
        }
        setIsLoading(false);
      })
      .catch(() => {
        setBasketError(
          (R.path(["BasketErrors", 0], labels) ||
            labels.GenericErrorText) as string,
        );
        setIsLoading(false);
      });
  };

  const renderPrice = (price: number | undefined) => {
    return Mustache.render(labels.PriceFormatText, {
      value: formatNumber(price ?? 0, {
        culture: labels.Culture,
        fractionDigitsCount: 2,
      }),
    });
  };

  const isBasketEmpty = !Boolean(basketData?.PerformanceGroups?.length);
  // Voucher baskets are voucher-only and carry no VAT, so hide the VAT row.
  const hasVoucher = Boolean(
    basketData?.PerformanceGroups?.some((group) =>
      group.BasketPackagesAndExtras?.some((bp) => bp.IsVoucher),
    ),
  );
  return isLoading ? (
    <Loader />
  ) : (
    <>
      <ConfirmModal
        isOpen={isEmailPopup}
        title={labels.ImportantText}
        message={labels.EmailValidatedMessage}
        onConfirm={() => setIsEmailPopup(false)}
        confirmText={labels.ViewBasketButtonText}
      />
      {isBasketEmpty && <div>{labels.EmptyBasketText}</div>}
      {!isBasketEmpty && (
        <>
          <div className="o-basket__list">
            {basketData?.PerformanceGroups.map((item, perfIndex) => (
              <div className="o-basket__performance-group" key={perfIndex}>
                <div className="o-basket__performance-subheading">
                  <h3>{item.SitecorePerformance}</h3>
                  <div>{item.SitecorePerformanceDescription}</div>
                </div>
                <div className="o-basket__performance-list">
                  {item.BasketPackagesAndExtras.map((basketPackage, index) =>
                    basketPackage.IsVoucher ? (
                      <VoucherItem
                        key={`${basketPackage.ItemId}-${index}`}
                        labels={labels}
                        basketPackage={basketPackage}
                        renderPrice={renderPrice}
                        updateBasketData={setBasketData}
                        domain={domain}
                        basketItemIndex={index}
                        performanceGroupIndex={perfIndex}
                        basketData={basketData}
                        setIsLoading={setIsLoading}
                      />
                    ) : (
                    <BasketItem
                      key={`${basketPackage.ItemId}-${index}`}
                      labels={labels}
                      basketPackage={basketPackage}
                      renderPrice={renderPrice}
                      updateBasketData={setBasketData}
                      domain={domain}
                      basketItemIndex={index}
                      performanceGroupIndex={perfIndex}
                      basketData={basketData}
                      setIsLoading={setIsLoading}
                      canBeDeleted={
                        !basketPackage.FullPrice ||
                        item.BasketPackagesAndExtras.filter(
                          (bp) =>
                            bp.PurchaseConstraint
                              ?.RequiredAudienceSubCategoryId ===
                              basketPackage.AudienceSubCategoryId &&
                            bp.ProductId === basketPackage.ProductId,
                        ).length < 1
                      }
                      onErrorChange={handleErrorChange}
                    />
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: isTablet ? "column" : "row",
              gap: "16px",
              justifyContent: "space-between",
              alignItems: "stretch",
            }}
          >
            <div className="o-basket__discount">
              <svg
                width="24"
                height="22"
                viewBox="0 0 24 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.875 17.2116V19.5912C1.875 19.6875 1.9151 19.7756 1.99531 19.8556C2.07531 19.9358 2.16344 19.9759 2.25969 19.9759H21.4903C21.5866 19.9759 21.6747 19.9358 21.7547 19.8556C21.8349 19.7756 21.875 19.6875 21.875 19.5912V17.2116H1.875ZM2.25969 4.375H5.71875C5.61458 4.1875 5.53688 3.98958 5.48563 3.78125C5.43438 3.57292 5.40875 3.35417 5.40875 3.125C5.40875 2.25167 5.71125 1.5125 6.31625 0.907499C6.92125 0.302499 7.66042 0 8.53375 0C9.05771 0 9.5424 0.136146 9.98781 0.408438C10.4332 0.680729 10.8219 1.01896 11.1538 1.42312L11.8509 2.35594L12.5481 1.42312C12.8671 1.00333 13.2531 0.661146 13.7062 0.396563C14.1596 0.132188 14.6478 0 15.1709 0C16.0426 0 16.7808 0.302499 17.3856 0.907499C17.9906 1.5125 18.2931 2.25167 18.2931 3.125C18.2931 3.35417 18.2695 3.57292 18.2222 3.78125C18.1749 3.98958 18.0952 4.1875 17.9831 4.375H21.4903C22.1218 4.375 22.6562 4.59375 23.0938 5.03125C23.5312 5.46875 23.75 6.00323 23.75 6.63469V19.5912C23.75 20.2227 23.5312 20.7572 23.0938 21.1947C22.6562 21.6322 22.1218 21.8509 21.4903 21.8509H2.25969C1.62823 21.8509 1.09375 21.6322 0.65625 21.1947C0.21875 20.7572 0 20.2227 0 19.5912V6.63469C0 6.00323 0.21875 5.46875 0.65625 5.03125C1.09375 4.59375 1.62823 4.375 2.25969 4.375ZM1.875 13.9903H21.875V6.63469C21.875 6.53844 21.8349 6.45031 21.7547 6.37031C21.6747 6.2901 21.5866 6.25 21.4903 6.25H14.6584L17.2116 9.74031L15.7162 10.8172L11.8509 5.57219L7.98563 10.8172L6.49031 9.74031L9.005 6.25H2.25969C2.16344 6.25 2.07531 6.2901 1.99531 6.37031C1.9151 6.45031 1.875 6.53844 1.875 6.63469V13.9903ZM8.53375 4.375C8.88792 4.375 9.18479 4.25521 9.42437 4.01562C9.66396 3.77604 9.78375 3.47917 9.78375 3.125C9.78375 2.77083 9.66396 2.47396 9.42437 2.23438C9.18479 1.99479 8.88792 1.875 8.53375 1.875C8.17958 1.875 7.88271 1.99479 7.64312 2.23438C7.40354 2.47396 7.28375 2.77083 7.28375 3.125C7.28375 3.47917 7.40354 3.77604 7.64312 4.01562C7.88271 4.25521 8.17958 4.375 8.53375 4.375ZM15.1681 4.375C15.5223 4.375 15.8192 4.25521 16.0588 4.01562C16.2983 3.77604 16.4181 3.47917 16.4181 3.125C16.4181 2.77083 16.2983 2.47396 16.0588 2.23438C15.8192 1.99479 15.5223 1.875 15.1681 1.875C14.814 1.875 14.5171 1.99479 14.2775 2.23438C14.0379 2.47396 13.9181 2.77083 13.9181 3.125C13.9181 3.47917 14.0379 3.77604 14.2775 4.01562C14.5171 4.25521 14.814 4.375 15.1681 4.375Z"
                  fill="#1C1B1F"
                />
              </svg>
              <p>{labels?.DiscountCodeText}</p>
              <span>{labels?.DiscountText}</span>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: isTablet ? "8px" : "16px",
                  justifyContent: isTablet ? "flex-end" : undefined,
                }}
              >
                <span
                  style={{
                    width: isTablet ? "160px" : "180px",
                    textAlign: "right",
                    fontSize: isTablet ? "14px" : "18px",
                  }}
                >
                  {`${hasVoucher ? labels.SubTotalText : labels.SubTotalExcVatText}:`}
                </span>
                <span
                  style={{
                    width: "150px",
                    textAlign: "right",
                    fontSize: isTablet ? "14px" : "18px",
                  }}
                >
                  {renderPrice(basketData?.TotalExcVAT)}
                </span>
              </div>
              {!hasVoucher && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    justifyContent: isTablet ? "flex-end" : undefined,
                  }}
                >
                  <span
                    style={{
                      width: "180px",
                      textAlign: "right",
                      fontSize: isTablet ? "14px" : "18px",
                    }}
                  >
                    {`${labels.VatPercentText} ${basketData?.VATPercent ?? 0}%:`}
                  </span>
                  <span
                    style={{
                      width: "150px",
                      textAlign: "right",
                      fontSize: isTablet ? "14px" : "18px",
                    }}
                  >
                    {renderPrice(
                      basketData?.TotalIncVAT && basketData?.TotalExcVAT
                        ? basketData?.TotalIncVAT - basketData?.TotalExcVAT
                        : 0,
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>

          <hr></hr>

          <div className="o-basket__bottom-bar-anchor" />
          <div className="o-basket__bottom-bar-wrapper">
            <div className="o-basket__bottom-bar u-sticky u-sticky--bottom">
              <div className="o-basket__bottom-bar-content">
                <div className="o-basket__bottom-bar-info">{`${labels.BasketTotalText} (${basketData?.TotalItems})`}</div>
                <div className="o-basket__total-price">
                  <h4>{`${labels.TotalText}: ${renderPrice(
                    basketData?.TotalIncVAT ?? 0,
                  )}`}</h4>
                  {!hasVoucher && <span>{labels.IncludingVatText}</span>}
                </div>
                <button
                  className="a-button o-basket__checkout-btn"
                  onClick={onCheckout}
                  disabled={hasAnyErrors}
                >
                  <span>{labels.CheckoutButtonText}</span>
                </button>
              </div>
            </div>
          </div>

          {basketData?.AmexOnly ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "32px",
                flexWrap: "wrap",
                alignItems: "center",
                paddingTop: "40px",
              }}
            >
              <img
                style={{ maxHeight: "50px", width: "auto" }}
                src={labels.AmexExclusiveIcon}
                alt="Amex Icon"
              />
              <span>{labels.AmexExclusiveMessage}</span>
            </div>
          ) : (
            <div className="o-payment-summary__images">
              <div className="o-payment-summary__images-wrapper">
                {labels.Cards.map((card) => (
                  <img src={card.ImageUrl} alt={card.Title} key={card.Title} />
                ))}
              </div>
              <div className="o-payment-summary__checkout-text">
                <i className="zmdi zmdi-lock-outline"></i>
                <span>{labels.SecurePaymentText}</span>
              </div>
            </div>
          )}
        </>
      )}
      <ConfirmModal
        isOpen={Boolean(basketError) && !isEmailPopup}
        title={labels.GenericErrorText}
        message={basketError}
        onConfirm={() => {
          setBasketError("");
          setQueryParams({});
        }}
        confirmText={labels.ViewBasketButtonText}
      />
    </>
  );
}

export default BasketPage;
